import type p5 from 'p5'
import { BaseHandle } from '../base/baseHandle'
import type { Point } from '../../types'
import { MARKER_DEFAULT_STROKE_COLOR, MARKER_HANDLE_DEFAULT_STROKE_WIDTH } from '../../constants'

class SquareHandle extends BaseHandle {
  _strokeWeight: number
  size: number
  strokeColor: string
  hoveredStrokeWeight: number

  constructor(p: p5, position: Point, size: number, strokeWeight: number) {
    super(p)
    this.position = position
    this.size = size
    this.strokeWeight = strokeWeight || MARKER_HANDLE_DEFAULT_STROKE_WIDTH
    this.strokeColor = MARKER_DEFAULT_STROKE_COLOR
  }

  /**
   * Get position values that will offset the original position by the markers size.
   * Rectangle shapes are drawn with their top-left corner positioned at the position values provided
   * By offsetting position relative to their height a position that will center the square relative
   * to the original position values is returned.
   */
  getCenteredPosition(): Point {
    return {
      x: this.position.x - this.size / 2,
      y: this.position.y - this.size / 2,
    }
  }

  isMousedOver() {
    const localCoordinates = this.getLocalCoordinates()
    const mouseX = localCoordinates.x
    const mouseY = localCoordinates.y
    const centeredPosition = this.getCenteredPosition()

    const isMousedOver =
      mouseX >= centeredPosition.x &&
      mouseX <= centeredPosition.x + this.size &&
      mouseY >= centeredPosition.y &&
      mouseY <= centeredPosition.y + this.size

    return isMousedOver
  }

  setupStroke() {
    this.p.strokeWeight(this.isMousedOver() ? this.hoveredStrokeWeight : this.strokeWeight)
    this.p.stroke(this.strokeColor)
    this.p.fill('#FFFFFF')
  }

  render() {
    const centeredPosition = this.getCenteredPosition()
    this.setupStroke()
    this.p.rect(centeredPosition.x, centeredPosition.y, this.size, this.size)
  }

  set strokeWeight(newStrokeWeight: number) {
    this._strokeWeight = newStrokeWeight
    this.hoveredStrokeWeight = this.strokeWeight * 0.5
  }

  get strokeWeight(): number {
    return this._strokeWeight
  }
}

export { SquareHandle }
